import { Phone, Mail, MapPin, Instagram, Facebook } from "lucide-react";

export default function Footer() {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer
      className="bg-[#1C1917] text-[#A8A29E] py-20 md:py-32 relative overflow-hidden"
      data-testid="footer"
    >
      {/* Watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
        <h2 className="font-['Outfit'] footer-watermark font-bold tracking-tighter opacity-30">
          JV RENOVATION
        </h2>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid md:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 font-['Outfit'] text-2xl font-semibold mb-4">
              <span className="text-[#22C55E] font-bold">JV</span>
              <span className="text-white">Home Renovation</span>
            </div>
            <p className="text-[#A8A29E] leading-relaxed mb-6">
              Quality construction and renovation services serving Santaquin and nearby areas. 
            </p>
            {/* Social Media */}
            <div className="flex gap-4">
              <a 
                href="#" 
                className="w-10 h-10 bg-white/10 hover:bg-[#166534] flex items-center justify-center transition-colors"
                data-testid="social-instagram"
                title="Instagram - Coming Soon"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61570809616017" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 hover:bg-[#166534] flex items-center justify-center transition-colors"
                data-testid="social-facebook"
                title="Follow us on Facebook"
              >
                <Facebook size={20} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-white/10 hover:bg-[#166534] flex items-center justify-center transition-colors"
                data-testid="social-tiktok"
                title="TikTok - Coming Soon"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Outfit'] text-white text-lg font-medium mb-4">
              Quick Links
            </h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection("services")}
                  className="hover:text-white transition-colors"
                  data-testid="footer-services-link"
                >
                  Our Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("gallery")}
                  className="hover:text-white transition-colors"
                  data-testid="footer-gallery-link"
                >
                  Our Work
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("about")}
                  className="hover:text-white transition-colors"
                  data-testid="footer-about-link"
                >
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("quote")}
                  className="hover:text-white transition-colors"
                  data-testid="footer-quote-link"
                >
                  Get a Quote
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-['Outfit'] text-white text-lg font-medium mb-4">
              Services
            </h4>
            <ul className="space-y-3 text-sm">
              <li>Full Remodels</li>
              <li>Stucco</li>
              <li>Tile & Finishing</li>
              <li>Concrete</li>
              <li>Stone Masonry</li>
              <li>Painting</li>
              <li>And More...</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-['Outfit'] text-white text-lg font-medium mb-4">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <MapPin size={18} className="text-[#166534] flex-shrink-0" />
                <span>Santaquin, Utah & Nearby Areas</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={18} className="text-[#166534] flex-shrink-0 mt-1" />
                <div className="flex flex-col">
                  <a href="tel:+14358517485" className="hover:text-white transition-colors">
                    (435) 851-7485 <span className="text-xs text-[#57534E]">English</span>
                  </a>
                  <a href="tel:+14358514119" className="hover:text-white transition-colors">
                    (435) 851-4119 <span className="text-xs text-[#57534E]">Español</span>
                  </a>
                  <span className="text-xs text-[#57534E] mt-1">Call or Text</span>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#166534] flex-shrink-0" />
                <a href="mailto:Jvhomerenovationllc@gmail.com" className="hover:text-white transition-colors break-all text-sm">
                  Jvhomerenovationllc@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-[#292524] pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm">
            &copy; {new Date().getFullYear()} JV Home Renovation LLC. All rights reserved.
          </p>
          <p className="text-sm text-[#57534E]">
            Quality work, every time.
          </p>
        </div>
      </div>
    </footer>
  );
}
